import { watch, onMounted, onBeforeUnmount, type Ref } from 'vue'

/**
 * Patrón A: el padre controla visibilidad vía prop (v-model/open).
 * Usar en componentes con watch(prop).
 */
export function useModalHistoryProp(
  isOpen: Ref<boolean> | (() => boolean),
  onBack: () => void,
) {
  let _pushed = false
  let _prevState: unknown = null

  function close() {
    if (_pushed) {
      _pushed = false
      // replaceState instead of back() — avoids popstate that confuses Vue Router
      history.replaceState(_prevState, '')
    }
    onBack()
  }

  function onPopState() {
    const open = typeof isOpen === 'function' ? isOpen() : isOpen.value
    if (open) { _pushed = false; onBack() }
  }

  const stopWatch = watch(
    typeof isOpen === 'function' ? isOpen : () => (isOpen as Ref<boolean>).value,
    (val) => {
      if (val && !_pushed) {
        _prevState = history.state
        history.pushState({ _modal: true }, '')
        _pushed = true
      }
    },
  )

  onMounted(() => window.addEventListener('popstate', onPopState))
  onBeforeUnmount(() => {
    window.removeEventListener('popstate', onPopState)
    stopWatch()
  })

  return { close }
}

/**
 * Patrón B: el componente se monta solo cuando está abierto (v-if en el padre).
 * Usar en componentes donde mounted === open.
 */
export function useModalHistoryMount(onBack: () => void) {
  let _pushed = false
  let _prevState: unknown = null

  function close() {
    if (_pushed) {
      _pushed = false
      // replaceState instead of back() — avoids popstate that confuses Vue Router
      history.replaceState(_prevState, '')
    }
    onBack()
  }

  function onPopState() {
    if (_pushed) { _pushed = false; onBack() }
  }

  onMounted(() => {
    _prevState = history.state
    history.pushState({ _modal: true }, '')
    _pushed = true
    window.addEventListener('popstate', onPopState)
  })

  onBeforeUnmount(() => window.removeEventListener('popstate', onPopState))

  return { close }
}
